import { zodResolver } from '@hookform/resolvers/zod'
import { useCallback, useEffect } from 'react'
import { useForm } from 'react-hook-form'

import type { PromptTemplate } from '@/components/chat/prompt-template.interface'

import {
  defaultPromptTemplateFormValues,
  type PromptTemplateDrawerFormSchemaType,
  promptTemplateDrawerFormSchema,
} from './prompt-template-drawer-form.schema'

interface UsePromptTemplateDrawerProps {
  template: PromptTemplate | null
  onSave: (values: PromptTemplateDrawerFormSchemaType) => void
  onClose: () => void
}

interface UsePromptTemplateDrawerReturn {
  form: ReturnType<typeof useForm<PromptTemplateDrawerFormSchemaType>>
  isEditing: boolean
  handleSubmit: () => void
  handleCancel: () => void
}

export const usePromptTemplateDrawer = ({
  template,
  onSave,
  onClose,
}: UsePromptTemplateDrawerProps): UsePromptTemplateDrawerReturn => {
  const isEditing = template !== null

  const form = useForm<PromptTemplateDrawerFormSchemaType>({
    resolver: zodResolver(promptTemplateDrawerFormSchema),
    defaultValues: defaultPromptTemplateFormValues,
    mode: 'onBlur',
  })

  useEffect(() => {
    if (template) {
      form.reset({
        name: template.name,
        description: template.description,
        content: template.content,
      })
      return
    }
    form.reset(defaultPromptTemplateFormValues)
  }, [template, form])

  const handleSubmit = useCallback(() => {
    form.handleSubmit(onSave)()
  }, [form, onSave])

  const handleCancel = useCallback(() => {
    form.reset(defaultPromptTemplateFormValues)
    onClose()
  }, [form, onClose])

  return {
    form,
    isEditing,
    handleSubmit,
    handleCancel,
  }
}
